"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { ArrowRight, BadgeCheck, CheckCircle2, Clock, ShoppingBag } from "lucide-react";
import posthog from "posthog-js";

import { formatPrice, type Locale } from "@/config/locale";
import { formatDosesPerVialDisplay } from "@/lib/calculator/calculations";
import type { BuyBridgeProduct, BuyBridgeVariant } from "@/lib/calculator/buy-bridge";
import {
	CALCULATOR_DIRECT_ADD_FLAG,
	isAutomatedExperimentVisitor,
	isBuyBridgeVariantAvailable,
	isCalculatorDirectAddEligible,
	resolveBuyBridgeMatch,
	type CalculatorDirectAddVariant,
} from "@/lib/calculator/direct-add-experiment";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { resolveImageAlt } from "@/lib/images/resolve-image-alt";
import { cn } from "@/lib/utils";
import { InternalSourceLink } from "@/ui/components/analytics/internal-source-link";
import { BackInStockForm } from "@/ui/components/pdp/back-in-stock-form";
import { useAddToCart } from "@/ui/components/cart/use-add-to-cart";
import { useBridgeCtaTracking } from "./use-bridge-cta-tracking";

interface BuyBridgeCardProps {
	product: NonNullable<BuyBridgeProduct>;
	/** vial mg the user picked (currentValues.peptideAmount) */
	peptideAmount: number;
	/** currentValues.result.dosesPerVial */
	dosesPerVial: number;
	locale: string;
	channel: string;
	/**
	 * Suppress this card's OWN mobile bottom sticky bar. Set `true` inside the
	 * Peptidrechner: there the result-cockpit StickyPill already owns the mobile
	 * bottom-anchored slot, and two competing `fixed bottom` elements would stack.
	 * Defaults to `false` so any standalone reuse keeps the bar.
	 */
	disableMobileSticky?: boolean;
}

/**
 * €/Dosis label in the visitor's REFERENCE currency (HUF/PLN/…). Computed from the matched
 * variant's server-resolved `referenceAmount` divided by the runtime `dosesPerVial`, then
 * formatted via `formatPrice` (a pricing home — the money-format ESLint rule exempts it; we
 * NEVER hand-roll `Intl.NumberFormat({ style: "currency" })`). Returns null unless the match
 * is the EXACT vial the user calculated with (dosesPerVial refers to THAT vial, so dividing
 * an upsold larger vial's price would misstate per-dose cost). Exported so the cockpit's
 * green "pro Dosis" tile and the card's spec strip show the identical anchor.
 */
export function computeBuyBridgePerDoseLabel(
	product: NonNullable<BuyBridgeProduct>,
	peptideAmount: number,
	dosesPerVial: number,
	locale: string,
): string | null {
	const matched = resolveBuyBridgeMatch(product, peptideAmount);
	if (!matched || matched.doseMg !== peptideAmount) return null;
	if (matched.referenceAmount === null || !matched.referenceCurrency || dosesPerVial <= 0) return null;
	const perDose = matched.referenceAmount / dosesPerVial;
	if (!Number.isFinite(perDose) || perDose <= 0) return null;
	return formatPrice(perDose, matched.referenceCurrency, locale as Locale);
}

/**
 * "Befund-Bruecke" buy card (Direction A). Runs the dose→variant match purely on the
 * server-passed `product` prop + the user's currentValues (NO client fetch → preserves
 * the React #418 fix, zero CLS). Deep-links the matched vial to the PDP via ?variant=.
 */
export function BuyBridgeCard({
	product,
	peptideAmount,
	dosesPerVial,
	locale,
	channel,
	disableMobileSticky = false,
}: BuyBridgeCardProps) {
	const t = useTranslations("peptideCalculator.buyBridge");
	const tCalculator = useTranslations("peptideCalculator");
	const tStock = useTranslations("stock");
	const addToCart = useAddToCart();
	const cardRef = useRef<HTMLDivElement | null>(null);
	const viewedPeptidesRef = useRef(new Set<string>());
	const eligibleMatchesRef = useRef(new Set<string>());
	const evaluatedMatchesRef = useRef(new Set<string>());
	const addLockRef = useRef(false);
	const [stickyVisible, setStickyVisible] = useState(false);
	const [addPending, setAddPending] = useState(false);
	const [assignment, setAssignment] = useState<{
		key: string;
		variant: CalculatorDirectAddVariant;
	} | null>(null);

	// --- match algorithm (client; uses only currentValues + product prop) ---
	// Exact-or-upsell-or-largest (never null when a dosed variant is in stock), so
	// off-grid calculator presets still surface a buyable vial. Picks among IN-STOCK
	// variants only (smallest pack first among same-dose) — see `pickMatchedVariant`.
	const matched = useMemo<BuyBridgeVariant | null>(
		() => resolveBuyBridgeMatch(product, peptideAmount),
		[product, peptideAmount],
	);

	// Clean, crawlable PDP URL. The PDP visit is still attributed to the calculator (so a
	// calculator→purchase funnel is measurable by property, not just same-person path
	// stitching): `InternalSourceLink` hands the source over on click.
	const href = matched
		? `/${locale}/${channel}/products/${product.productSlug}?variant=${matched.id}`
		: `/${locale}/${channel}/products/${product.productSlug}`;

	const priceLabel = matched ? matched.priceLabel : product.fromPriceLabel;

	// --- per-dose anchor in the REFERENCE currency (shared with the cockpit tile) ---
	// Only honest on an EXACT dose match: dosesPerVial is computed from the user's chosen
	// vial size, so dividing an upsold (larger) vial's price by it would misstate per-dose
	// cost. `computeBuyBridgePerDoseLabel` enforces that guard + routes through formatPrice.
	const perDoseLabel = useMemo<string | null>(
		() => computeBuyBridgePerDoseLabel(product, peptideAmount, dosesPerVial, locale),
		[product, peptideAmount, dosesPerVial, locale],
	);

	// --- stock honesty: product-level OOS comes from the server (`product.inStock`), but
	// the matched variant may itself be sold out even when SOME variant is in stock. The
	// card's CTA is replaced by the back-in-stock form whenever nothing buyable is matched.
	const inStock = matched ? isBuyBridgeVariantAvailable(matched) : product.inStock;

	const thumbnailUrl = product.thumbnailUrl ? normalizeSaleorMediaUrl(product.thumbnailUrl) : null;
	const matchedMg = matched?.doseMg ?? peptideAmount;

	const buyCtaLabel = tCalculator("spoke.viewProduct");

	// Shipping promise only on a CONFIRMED positive count from Saleor. Untracked stock
	// (quantityAvailable === null) still sells, but it is not evidence that we can ship today.
	const readyToShip =
		matched !== null && typeof matched.quantityAvailable === "number" && matched.quantityAvailable > 0;

	const isExactMatch = matched !== null && matched.doseMg === peptideAmount;
	const directAddEligible = isCalculatorDirectAddEligible(product, matched, peptideAmount);
	const eligibilityKey = directAddEligible ? `${product.calcSlug}:${matched.id}:${peptideAmount}` : null;
	// Rendering falls back to the existing PDP journey while flags are unavailable,
	// but analytics must not label that visitor as `control` before PostHog emitted
	// the real `$feature_flag_called` exposure.
	const experimentVariant =
		assignment?.key === eligibilityKey ? assignment.variant : ("not_exposed" as const);
	const trackingProperties = {
		bridge_surface: "result_card" as const,
		locale,
		channel,
		peptide_slug: product.calcSlug,
		product_slug: product.productSlug,
		variant_id: matched?.id ?? null,
		match_type: !matched
			? ("none" as const)
			: isExactMatch
				? ("exact" as const)
				: (matched.doseMg ?? 0) > peptideAmount
					? ("larger_vial" as const)
					: ("smaller_vial" as const),
	};
	const { setTrackedElement: setPdpElement, trackClick: trackPdpClick } = useBridgeCtaTracking({
		...trackingProperties,
		cta_action: "view_product",
	});
	const { setTrackedElement: setDirectElement, trackClick: trackDirectClick } = useBridgeCtaTracking({
		...trackingProperties,
		cta_action: "direct_add",
	});

	// --- card_viewed (IntersectionObserver, once per peptide in this stable calculator) ---
	useEffect(() => {
		const node = cardRef.current;
		if (!node || viewedPeptidesRef.current.has(product.calcSlug)) return;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !viewedPeptidesRef.current.has(product.calcSlug)) {
						viewedPeptidesRef.current.add(product.calcSlug);
						posthog.capture("card_viewed", {
							peptide_slug: product.calcSlug,
							match_type: isExactMatch ? "exact" : matched ? "larger_vial" : "none",
							channel,
						});
						observer.disconnect();
					}
				}
			},
			{ threshold: 0.4 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [product.calcSlug, channel, isExactMatch, matched]);

	// Record the eligibility denominator after a genuinely eligible card stayed at least 40%
	// visible for 500 ms. Afterwards `onFeatureFlags` avoids the undefined-as-control race.
	// We only call getFeatureFlag when the flag exists, so PostHog's automatic exposure event
	// remains authoritative and the pre-experiment stability window creates no fake exposure.
	useEffect(() => {
		const node = cardRef.current;
		if (!node || !eligibilityKey || !matched) return;
		if (
			isAutomatedExperimentVisitor({
				webdriver: navigator.webdriver,
				userAgent: navigator.userAgent,
				isInternalUser:
					posthog.get_property("$internal_or_test_user") === true ||
					posthog.get_property("is_internal_user") === true,
				isTestUser: posthog.get_property("is_test_user") === true,
			})
		) {
			return;
		}

		let dwellTimer: ReturnType<typeof setTimeout> | null = null;
		let unsubscribeFlags: (() => void) | null = null;
		let active = true;

		const evaluateFlag = () => {
			if (!active) return;
			if (!eligibleMatchesRef.current.has(eligibilityKey)) {
				eligibleMatchesRef.current.add(eligibilityKey);
				posthog.capture("calculator_direct_add_eligible_viewed", {
					bridge_version: "calculator_bridge_v2",
					locale,
					product_slug: product.productSlug,
					peptide_slug: product.calcSlug,
					variant_id: matched.id,
					matched_dose_mg: matched.doseMg,
					channel,
				});
			}
			if (evaluatedMatchesRef.current.has(eligibilityKey) || unsubscribeFlags) return;

			let callbackHandled = false;
			const unsubscribe = posthog.onFeatureFlags((flags) => {
				if (!active || callbackHandled) return;
				// A missing flag or an incomplete first response is not an assignment.
				// Keep listening until a supported variant arrives or this effect ends.
				if (!flags.includes(CALCULATOR_DIRECT_ADD_FLAG)) return;
				const value = posthog.getFeatureFlag(CALCULATOR_DIRECT_ADD_FLAG);
				if (value !== "control" && value !== "direct_add") return;
				callbackHandled = true;
				evaluatedMatchesRef.current.add(eligibilityKey);
				setAssignment({ key: eligibilityKey, variant: value });
				unsubscribeFlags?.();
				unsubscribeFlags = null;
			});
			unsubscribeFlags = unsubscribe;
			// PostHog may invoke the callback synchronously when flags are already loaded.
			// In that case it ran before `unsubscribeFlags` received the returned cleanup.
			if (callbackHandled) {
				unsubscribe();
				unsubscribeFlags = null;
			}
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && entry.intersectionRatio >= 0.4) {
					if (!dwellTimer) dwellTimer = setTimeout(evaluateFlag, 500);
				} else if (dwellTimer) {
					clearTimeout(dwellTimer);
					dwellTimer = null;
				}
			},
			{ threshold: [0, 0.4] },
		);
		observer.observe(node);

		return () => {
			active = false;
			observer.disconnect();
			if (dwellTimer) clearTimeout(dwellTimer);
			unsubscribeFlags?.();
		};
	}, [channel, eligibilityKey, matched, product.calcSlug, product.productSlug, locale]);

	// --- sticky bar visibility: shown whenever the inline card is NOT in view ---
	// IntersectionObserver on the inline card (mirrors pdp/sticky-bar.tsx) — robust on
	// tall layouts where a fixed scrollY threshold misfires. Skipped entirely when the
	// host suppresses this card's mobile bar (the Peptidrechner StickyPill owns it).
	useEffect(() => {
		// No sticky buy bar when out of stock (its CTA is a buy link the OOS card hides).
		if (disableMobileSticky || !inStock) return;
		const node = cardRef.current;
		if (!node) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				setStickyVisible(!entry.isIntersecting);
			},
			{ threshold: 0 },
		);
		observer.observe(node);
		return () => observer.disconnect();
	}, [disableMobileSticky, inStock]);

	const handlePdpCtaClick = () => {
		trackPdpClick();
		posthog.capture("calculator_cta_clicked", {
			bridge_version: "calculator_bridge_v2",
			product_slug: product.productSlug,
			locale,
			cta_source: "buy_bridge",
			cta_action: "view_product",
			peptide_slug: product.calcSlug,
			variant_id: matched?.id ?? null,
			matched_dose_mg: matched?.doseMg ?? null,
			price_label: priceLabel,
			has_match: matched !== null,
			match_type: isExactMatch ? "exact" : matched ? "larger_vial" : "none",
			experiment_variant: experimentVariant,
			channel,
		});
	};

	const handleDirectAdd = useCallback(async () => {
		if (!matched || !directAddEligible || addLockRef.current) return;
		addLockRef.current = true;
		setAddPending(true);

		// The shared runner owns the transient drawer line, the canonical
		// attempt/failure events and the retry toast; the calculator keeps its own
		// funnel events on top via the lifecycle hooks.
		await addToCart(
			{
				channel,
				variantId: matched.id,
				quantity: 1,
				productSlug: product.productSlug,
				productName: product.name,
				variantName: matched.name,
				internalSource: "calculator",
				internalSourceDetail: product.calcSlug,
				addSurface: "calculator_buy_bridge",
				experimentVariant: "direct_add",
				locale,
			},
			{
				onAttempt: (cartAddAttemptId) => {
					posthog.capture("calculator_cta_clicked", {
						bridge_version: "calculator_bridge_v2",
						locale,
						product_slug: product.productSlug,
						cta_source: "buy_bridge",
						cta_action: "direct_add",
						peptide_slug: product.calcSlug,
						variant_id: matched.id,
						matched_dose_mg: matched.doseMg,
						experiment_variant: "direct_add",
						cart_add_attempt_id: cartAddAttemptId,
						channel,
					});
					// Kept unchanged for the existing calculator insights; the canonical
					// cross-surface attempt event is emitted right after it.
					posthog.capture("calculator_direct_add_attempted", {
						bridge_version: "calculator_bridge_v2",
						locale,
						product_slug: product.productSlug,
						peptide_slug: product.calcSlug,
						variant_id: matched.id,
						cart_add_attempt_id: cartAddAttemptId,
						channel,
					});
				},
				onSuccess: (cartAddAttemptId) => {
					posthog.capture("calculator_direct_add_succeeded", {
						bridge_version: "calculator_bridge_v2",
						locale,
						product_slug: product.productSlug,
						peptide_slug: product.calcSlug,
						variant_id: matched.id,
						cart_add_attempt_id: cartAddAttemptId,
						channel,
					});
				},
				onFailure: (cartAddAttemptId, errorCode) => {
					posthog.capture("calculator_direct_add_failed", {
						bridge_version: "calculator_bridge_v2",
						locale,
						product_slug: product.productSlug,
						peptide_slug: product.calcSlug,
						variant_id: matched.id,
						cart_add_attempt_id: cartAddAttemptId,
						error_code: errorCode,
						channel,
					});
				},
			},
		);

		addLockRef.current = false;
		setAddPending(false);
	}, [addToCart, channel, directAddEligible, locale, matched, product]);

	// Honesty guard shared by hook + doses/per-dose specs: only when the match is the
	// exact vial the user calculated with (dosesPerVial refers to THAT vial).
	const showDoses = isExactMatch && dosesPerVial > 0;

	return (
		<>
			<div
				ref={cardRef}
				data-testid="buy-bridge-card"
				className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
			>
				<div className="bg-muted/30 flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2 text-xs">
					<span className="inline-flex items-center gap-1.5 font-semibold text-primary">
						{inStock ? (
							<BadgeCheck className="h-4 w-4" aria-hidden />
						) : (
							<Clock className="h-4 w-4" aria-hidden />
						)}
						{!inStock ? t("comingSoon") : isExactMatch ? t("matchBadge") : tCalculator("spoke.viewProduct")}
					</span>
					{inStock && <span className="font-medium text-success">{t("inStock")}</span>}
				</div>
				<div className="space-y-4 p-4 sm:p-5">
					<div className="flex items-center gap-3">
						{thumbnailUrl && (
							<div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-border bg-card">
								<Image
									src={thumbnailUrl}
									alt={resolveImageAlt(product.thumbnailAlt, product.name)}
									fill
									className="object-contain"
									sizes="64px"
								/>
							</div>
						)}
						<div className="min-w-0">
							<h3 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
								{product.name}
							</h3>
							{matched && (
								<p className="mt-1 text-sm text-muted-foreground">
									{t("matchedVial")} ·{" "}
									<span className="font-mono font-semibold text-foreground">{matchedMg} mg</span>
								</p>
							)}
						</div>
					</div>
					{showDoses && (
						<p className="text-sm text-muted-foreground">
							{t("hook", { mg: matchedMg, doses: formatDosesPerVialDisplay(dosesPerVial) })}
						</p>
					)}
					{perDoseLabel && (
						<p className="text-sm font-medium text-foreground">{t("perDose", { price: perDoseLabel })}</p>
					)}
					{inStock ? (
						<div className="flex flex-wrap items-center justify-between gap-3">
							<span className="text-2xl font-bold tabular-nums tracking-tight text-foreground">
								{priceLabel}
							</span>
							{experimentVariant === "direct_add" && directAddEligible ? (
								<button
									ref={setDirectElement}
									type="button"
									disabled={addPending}
									aria-busy={addPending}
									onClick={() => {
										trackDirectClick();
										void handleDirectAdd();
									}}
									className="hover:bg-primary/90 inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-center text-sm font-semibold text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60 sm:flex-none"
								>
									<ShoppingBag className={cn("h-4 w-4", addPending && "animate-pulse")} aria-hidden />
									{t("cta")}
								</button>
							) : (
								<InternalSourceLink
									ref={setPdpElement}
									href={href}
									source="calculator"
									detail={product.calcSlug}
									onClick={handlePdpCtaClick}
									data-testid="buy-bridge-buy-cta"
									className="hover:bg-primary/90 inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-center text-sm font-semibold text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:flex-none"
								>
									{buyCtaLabel}
									<ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
								</InternalSourceLink>
							)}
						</div>
					) : (
						<BackInStockForm
							productSlug={product.productSlug}
							variantId={matched?.id}
							locale={locale}
							channel={channel}
							source="calculator_buy_bridge"
						/>
					)}
					<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
						{readyToShip && (
							<span className="inline-flex items-center gap-1 font-medium text-success">
								<CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
								{tStock("inStock")}
							</span>
						)}
						{experimentVariant === "direct_add" && directAddEligible ? (
							<InternalSourceLink
								ref={setPdpElement}
								href={href}
								source="calculator"
								detail={product.calcSlug}
								onClick={handlePdpCtaClick}
								className="inline-flex min-h-11 items-center underline underline-offset-4"
							>
								{t("learnMore")}
							</InternalSourceLink>
						) : (
							<InternalSourceLink
								href={`/${locale}/${channel}/products/${product.productSlug}`}
								source="calculator"
								detail={product.calcSlug}
								className="inline-flex min-h-11 items-center underline underline-offset-4"
							>
								{t("otherSize")}
							</InternalSourceLink>
						)}
					</div>
				</div>
				<div className="border-t border-border px-4 py-2 text-xs font-medium text-accent-dark">
					<span className="inline-flex items-center gap-1">
						<CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
						{t("labTested")}
					</span>
				</div>
			</div>

			{/* Mobile sticky bar — shown once the inline card scrolls out of the thumb zone.
			    Suppressed inside the Peptidrechner: the result-cockpit StickyPill owns the
			    mobile bottom slot there, so we never stack two `fixed bottom` bars. */}
			{stickyVisible && !disableMobileSticky && inStock && (
				<div
					className={cn(
						"bg-card/95 fixed inset-x-0 bottom-0 z-30 border-t border-border px-4 pt-3 shadow-lg backdrop-blur md:hidden",
						"pb-[max(0.75rem,env(safe-area-inset-bottom))]",
					)}
				>
					<div className="flex items-center gap-3">
						{thumbnailUrl && (
							<div className="relative h-10 w-10 shrink-0 overflow-hidden rounded bg-secondary">
								<Image
									src={thumbnailUrl}
									alt={resolveImageAlt(product.thumbnailAlt, product.name)}
									fill
									className="object-cover"
									sizes="40px"
								/>
							</div>
						)}
						<div className="min-w-0 flex-1">
							<p className="truncate text-xs text-muted-foreground">
								{product.name} · <span className="font-mono">{matchedMg} mg</span>
							</p>
							<p className="font-mono text-sm font-semibold tabular-nums text-foreground">{priceLabel}</p>
						</div>
						{experimentVariant === "direct_add" && directAddEligible ? (
							<button
								type="button"
								onClick={() => void handleDirectAdd()}
								disabled={addPending}
								aria-busy={addPending}
								className="hover:bg-primary/90 shadow-primary/20 inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-md transition-all disabled:opacity-60"
							>
								<ShoppingBag
									className={cn("h-4 w-4", addPending && "animate-pulse")}
									strokeWidth={2}
									aria-hidden
								/>
								{t("ctaShort")}
							</button>
						) : (
							<InternalSourceLink
								href={href}
								source="calculator"
								detail={product.calcSlug}
								onClick={handlePdpCtaClick}
								className="hover:bg-primary/90 shadow-primary/20 group inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-md transition-all"
							>
								{tCalculator("spoke.viewProduct")}
								<ArrowRight
									className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-0.5"
									strokeWidth={2}
									aria-hidden
								/>
							</InternalSourceLink>
						)}
					</div>
				</div>
			)}
		</>
	);
}

interface BuyBridgeGateProps {
	/** server-resolved product; null ⇒ gate failed ⇒ always render fallback */
	product: BuyBridgeProduct;
	peptideAmount: number;
	dosesPerVial: number;
	locale: string;
	channel: string;
	/** the existing learn-more link, rendered whenever the card is not shown */
	fallback: ReactNode;
	/** forwarded to BuyBridgeCard — suppress its own mobile sticky bar (StickyPill wins). */
	disableMobileSticky?: boolean;
}

/**
 * Decides between the BuyBridgeCard and the legacy learn-more link.
 *
 * Renders the card when product != null (server-resolved purchasable product).
 * Otherwise renders `fallback` (graceful degradation — never a 404).
 *
 * The former PostHog feature-flag gate (`calculator-buy-bridge`) has been
 * removed: the flag is at 100% rollout with no active experiment, and the
 * async flag-resolution race caused ~44% of sessions to be misclassified as
 * `buy_bridge_suppressed { reason: "flag_off" }` before flags loaded.
 * The product/stock gate in `resolveBuyBridgeProductSlug` / `getBuyBridgeProduct`
 * is the real gate; flag-level gating adds no value here.
 *
 * `?bb=0` query param opts the card out (owner escape-hatch).
 */
export function BuyBridgeGate({ product, fallback, ...cardProps }: BuyBridgeGateProps) {
	const searchParams = useSearchParams();
	const trackedKeyRef = useRef<string | null>(null);

	// ?bb=0 lets the owner suppress the card on any page without a code deploy.
	const bbSuppressed = searchParams.get("bb") === "0";
	const showCard = product != null && !bbSuppressed;

	useEffect(() => {
		const calcSlug = product?.calcSlug ?? null;
		const trackingKey = `${calcSlug ?? "null"}:${showCard ? "shown" : bbSuppressed ? "bb_param_off" : "null_product"}`;
		if (trackedKeyRef.current === trackingKey) return;
		trackedKeyRef.current = trackingKey;
		if (showCard) {
			posthog.capture("buy_bridge_shown", {
				calc_slug: calcSlug,
				channel: cardProps.channel,
				locale: cardProps.locale,
				bridge_version: "calculator_bridge_v2",
			});
		} else {
			posthog.capture("buy_bridge_suppressed", {
				reason: bbSuppressed ? "bb_param_off" : "null_product",
				calc_slug: calcSlug,
				channel: cardProps.channel,
			});
		}
	}, [bbSuppressed, cardProps.channel, cardProps.locale, product, showCard]);

	if (!showCard || product == null) return <>{fallback}</>;

	return <BuyBridgeCard product={product} {...cardProps} />;
}
